import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import CareersApplyForm from '../elements/careers-apply-form';

// Premium design system
import {
  PremiumHero,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FaqAccordion,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

import careersData from '../../data/careers.json';

const OPENINGS = careersData.openings || [];
const CANONICAL = 'https://www.ridenrepair.com/careers';

const VALUES = [
  {
    icon: '🔧',
    title: 'The work is real',
    desc: 'Every ticket you close is a vehicle that runs again and an owner who got their day back. Nothing here is theoretical.',
  },
  {
    icon: '📈',
    title: 'Ownership from day one',
    desc: 'Small teams, real numbers. You will own an outcome — a city, a funnel, a system — not a slice of someone else’s slide.',
  },
  {
    icon: '🎓',
    title: 'We train, then we trust',
    desc: 'Structured onboarding, skill certification for mechanics, and playbooks for every role. Then we get out of your way.',
  },
  {
    icon: '🛡️',
    title: 'Honest by design',
    desc: 'Transparent pricing, genuine parts, a 30-day warranty. The same standard we hold for customers, we hold internally.',
  },
  {
    icon: '🚀',
    title: 'Speed over ceremony',
    desc: 'Decisions in days, not quarters. If something is broken, the person who noticed it is empowered to fix it.',
  },
  {
    icon: '🇮🇳',
    title: 'Built for India',
    desc: 'From metros to tier-2 cities, on mobile data, in the customer’s own language. We build for how India actually rides.',
  },
];

const PROCESS = [
  { no: '01', icon: '📝', title: 'Apply', desc: 'Send us your details and resume. Every application is read by a human — no keyword filter decides your fate.' },
  { no: '02', icon: '📞', title: 'Intro call', desc: 'A 15–20 minute conversation about your experience and what you want next. Shortlisted candidates hear back within 7 working days.' },
  { no: '03', icon: '🛠️', title: 'Skill round', desc: 'A practical assessment for your craft — a hands-on service check for mechanics, a real problem for everyone else.' },
  { no: '04', icon: '🤝', title: 'Offer', desc: 'Team fit conversation, then a clear written offer with compensation, structure and a start date.' },
];

const FAQS = [
  {
    q: 'How do I apply for a job at Ride N Repair?',
    a: 'Pick a role from the open positions on this page and submit the application form with your resume. If nothing matches, choose "Something else — open application" and tell us what you do best.',
  },
  {
    q: 'Do I need experience to become a Ride N Repair mechanic?',
    a: 'For two-wheeler roles we look for at least one year of hands-on servicing experience across brands. Four-wheeler roles need two years or more. An ITI or diploma in an automobile or mechanical trade helps but is not mandatory if your practical skill is strong.',
  },
  {
    q: 'Which cities are you hiring in?',
    a: 'We service 32+ Indian cities. Active hiring is concentrated in Bengaluru, Delhi NCR (Delhi, Gurugram, Noida), Pune, Hyderabad, Mumbai and Chennai. Each role listing shows its own locations.',
  },
  {
    q: 'Is this a full-time job or gig work?',
    a: 'Every role listed here is a full-time position. Field mechanics work an assigned city with a fixed schedule, structured payouts and incentives — not ad-hoc gig shifts.',
  },
  {
    q: 'What happens after I submit my application?',
    a: 'You get a reference number immediately. Our team reviews applications and contacts shortlisted candidates within 7 working days for an introductory call.',
  },
  {
    q: 'Can freshers apply?',
    a: 'Yes. Inside Sales and Service Advisor roles are open to freshers with strong communication skills. Field mechanic and engineering roles need prior hands-on experience.',
  },
];

class Careers extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    const jobsForForm = OPENINGS.map((j) => ({ slug: j.slug, title: j.title, department: j.department }));
    const totalOpenings = OPENINGS.reduce((sum, j) => sum + (j.openings_count || 1), 0);

    // ItemList of the live roles — the crawlable index that points Google at each
    // job page, where the full JobPosting markup lives (one posting per URL, as
    // Google for Jobs requires).
    const schema = [
      {
        '@type': 'ItemList',
        name: 'Open roles at Ride N Repair',
        numberOfItems: OPENINGS.length,
        itemListElement: OPENINGS.map((job, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: job.title,
          url: `${CANONICAL}/${job.slug}`,
        })),
      },
      {
        '@type': 'Organization',
        name: 'Ride N Repair',
        url: 'https://www.ridenrepair.com',
        logo: 'https://www.ridenrepair.com/logo192.png',
        description: 'Doorstep bike and car service & repair across 32+ cities in India.',
        foundingDate: '2022',
      },
    ];

    return (
      <div className="rr-home rr-home--lux rr-careers">
        <Helmet>
          <link rel="canonical" href={CANONICAL} />
          <title>Careers at Ride N Repair — Jobs for Mechanics, Ops, Sales &amp; Engineers</title>
          <meta
            name="description"
            content="Join Ride N Repair. Open jobs for two-wheeler and car mechanics, service advisors, city operations, inside sales, engineering and marketing across Bengaluru, Delhi NCR, Pune, Hyderabad and more. Apply online in 2 minutes."
          />
          <meta name="robots" content="index,follow" />
          <meta property="og:title" content="Careers at Ride N Repair" />
          <meta
            property="og:description"
            content="Build India's most trusted doorstep vehicle service company. Open roles for mechanics, operations, sales, marketing and engineering."
          />
          <meta property="og:url" content={CANONICAL} />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="en_IN" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Careers at Ride N Repair" />
          <meta
            name="twitter:description"
            content="Open roles for mechanics, operations, sales, marketing and engineering across India."
          />
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge="Careers"
          title={<>Fix What India Rides On</>}
          subtitle="We bring certified mechanics to 2,00,000+ customers across 32+ cities — at their doorstep, with genuine parts and a 30-day warranty. Come build the rest of it with us."
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'See open roles', href: '#open-roles', variant: 'primary' },
            { label: 'Apply now', href: '#apply', variant: 'secondary' },
          ]}
          trustItems={[
            { value: `${totalOpenings}+`, label: 'Open Positions' },
            { value: '32+', label: 'Cities' },
            { value: '2,00,000+', label: 'Customers Served' },
            { value: '4.8★', label: 'Customer Rating' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Careers' }]} />

        {/* WHY US */}
        <SectionWrapper
          kicker="Why Ride N Repair"
          heading="A Company Built by People Who Actually Fix Things"
          subtitle="We are not a listings marketplace that hands you off to a garage. We hire the mechanics, we train them, we stand behind the work — and we build the systems that make it repeatable."
        >
          <WhyUsGrid items={VALUES} />
        </SectionWrapper>

        {/* OPEN ROLES */}
        <SectionWrapper
          id="open-roles"
          variant="gray"
          kicker="Open Roles"
          heading="Where We Are Hiring Right Now"
          subtitle={`${OPENINGS.length} roles across field operations, customer experience, growth, engineering and supply chain.`}
        >
          <div className="rr-jobs">
            {OPENINGS.map((job) => (
              <Link key={job.slug} to={`/careers/${job.slug}`} className="rr-job-card">
                <div className="rr-job-card__main">
                  <div className="rr-job-card__top">
                    <span className="rr-job-card__dept">{job.department}</span>
                    {job.openings_count > 1 && (
                      <span className="rr-job-card__count">{job.openings_count} openings</span>
                    )}
                  </div>
                  <h3 className="rr-job-card__title">{job.title}</h3>
                  <p className="rr-job-card__summary">{job.summary}</p>
                  <ul className="rr-job-card__meta">
                    <li>📍 {job.locations.slice(0, 3).join(' · ')}{job.locations.length > 3 ? ` +${job.locations.length - 3}` : ''}</li>
                    <li>🕒 {job.type_label}</li>
                    <li>📈 {job.experience}</li>
                  </ul>
                </div>
                <span className="rr-job-card__cta">
                  View role <span aria-hidden="true">→</span>
                </span>
              </Link>
            ))}
          </div>

          <div className="rr-jobs__none">
            <p>
              Nothing here that fits? We still want to hear from you — send an open application and tell us what
              you would own.
            </p>
            <a href="#apply" className="rr-cta-primary">Send an open application</a>
          </div>
        </SectionWrapper>

        {/* HIRING PROCESS */}
        <SectionWrapper
          variant="dark"
          kicker="Hiring Process"
          heading="Four Steps. No Ghosting."
          subtitle="You will always know where you stand. Shortlisted candidates hear from us within 7 working days."
        >
          <StepCards steps={PROCESS} />
        </SectionWrapper>

        {/* APPLY */}
        <SectionWrapper
          id="apply"
          kicker="Apply"
          heading="Send Us Your Application"
          subtitle="Two minutes. Attach a resume if you have one — if you don't, tell us about your work in your own words."
          narrow
        >
          <CareersApplyForm jobs={jobsForForm} source="web_careers_page" />
        </SectionWrapper>

        {/* FAQ */}
        <SectionWrapper variant="gray" kicker="FAQ" heading="Questions Candidates Ask Us" narrow>
          <FaqAccordion faqs={FAQS} />
        </SectionWrapper>

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default Careers;
